import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useBotRoute } from '../lib/useBotRoute';
import { KNOWLEDGE_TABS, knowledgeTab, type KnowledgeTab } from '../lib/builderForm';
import { DocumentsPanel } from '../components/knowledge/DocumentsPanel';
import { BuilderPanel } from '../components/knowledge/BuilderPanel';
import { PromptsPanel } from '../components/knowledge/PromptsPanel';
import { PagesPanel } from '../components/knowledge/PagesPanel';
import { SnapshotsPanel } from '../components/knowledge/SnapshotsPanel';
import { GlossaryPanel } from '../components/knowledge/GlossaryPanel';
import { BlocksPanel } from '../components/knowledge/BlocksPanel';

const TAB_LABEL: Record<KnowledgeTab, string> = {
  documents: 'Documents',
  builder: 'Builder',
  pages: 'Pages',
  snapshots: 'Snapshots',
  glossary: 'Glossary',
  blocks: 'Blocks',
  prompts: 'Prompts',
};

/**
 * Knowledge (A2, chatbot_v2_mono docs/superpowers/specs/2026-10-06-ai-console-v2-first-design.md): everything the bot
 * answers from, per (system, botId). Documents (step A); Builder — crawl → chunk via llm-tester — and per-bot Prompts
 * (step B); Pages and Snapshots (step C1); Latin glossary (step C3); Blocks the AI can send (knowledge→block plan 2).
 * gbrain follows. The tab is in the URL (`?tab=`).
 */
export function BotKnowledge() {
  const { system, botId } = useBotRoute();
  const [params, setParams] = useSearchParams();
  const tab = knowledgeTab(params.get('tab'));
  const key = `${system}/${botId}`;
  // A job started on Pages / Snapshots runs under Builder › Jobs (one place for progress and approvals).
  const [jobHint, setJobHint] = useState(false);
  const go = (t: KnowledgeTab) => {
    setJobHint(false);
    setParams(t === 'documents' ? {} : { tab: t });
  };
  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Knowledge</h1>
          <p className="text-sm text-gray-600">What the bot answers from. Documents are split into chunks and searched on every message.</p>
        </div>
        <div className="flex gap-1 border-b border-gray-200" role="tablist">
          {KNOWLEDGE_TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => go(t)}
              className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === t ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
        {/* Keyed by bot: nothing loaded for — or still in flight from — the previous bot reaches this one. */}
        {tab === 'documents' && <DocumentsPanel key={key} system={system} botId={botId} />}
        {tab === 'builder' && <BuilderPanel key={key} system={system} botId={botId} />}
        {tab === 'pages' && <PagesPanel key={key} system={system} botId={botId} onJobStarted={() => setJobHint(true)} />}
        {tab === 'snapshots' && <SnapshotsPanel key={key} system={system} botId={botId} onJobStarted={() => setJobHint(true)} />}
        {tab === 'glossary' && <GlossaryPanel key={key} system={system} botId={botId} />}
        {tab === 'blocks' && <BlocksPanel key={key} system={system} botId={botId} />}
        {tab === 'prompts' && <PromptsPanel key={key} system={system} botId={botId} />}
        {jobHint && (tab === 'pages' || tab === 'snapshots') && (
          <p className="text-sm">
            A job started —{' '}
            <button type="button" className="text-blue-600 hover:underline" onClick={() => go('builder')}>
              follow it in Builder › Jobs
            </button>
            .
          </p>
        )}
      </div>
    </div>
  );
}
